import { useBooking, useError, useSnack } from 'hooks';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import * as Yup from 'yup';
import { Form, Formik } from 'formik';
import { Legend, Stack } from '@koob/margaret';
import { KoobButton, KoobCard } from '@koob/ui';
import BookingSummary from 'components/Hotel/Booking/Edit/BookingSummary';
import { FormField, Text } from 'ui';
import { dayjs, formatDateTime } from '@koob/dates';
import { phoneRegex } from 'utils';
import CancelAdminBooking from 'components/Hotel/Booking/Edit/CancelBooking';
import CancellationPolicies from 'components/Hotel/Booking/Edit/CancellationPolicies';
import {
  calculateRoomNumberOfAdult,
  calculateTravelerKind,
  getRoomChildrenBirthdate,
} from 'utils/booking';
import { getBooking, updateBooking } from 'api/bookings';
import { BookingOwner, RoomPerson, ToInfos } from '../BookingInfos';
import HotelRoomCompositions from '../HotelRoomCompositions';
import { useEffect, useMemo, useState } from 'react';
import TripItem from './Trips/Partials/TripItem';
import KoobTitle from 'components/Koob/KoobTitle';
import { EditorField } from 'components/Fields';
import {
  ExperienceCancelConditions,
  HotelBookingPolicies,
} from '@koob/booking';
import {
  AdminBookingStateEnum,
  BenefitsKindEnum,
  BookingKindEnum,
} from '@koob/enums';
import { mergeCancelConditions, sumDeductionByConditions } from '@koob/utils';
import { useKoobStore } from 'store';
import { useAsync } from 'react-async';
import { getTripBookings } from 'api/trip';

const CANCELLATION_STATUS = {
  DMC: ['confirmed', 'sent', 'pending', 'pre_confirmed'],
  KOEDIA: ['confirmed'],
  RAKUTEN: ['confirmed'],
};

const IS_DISABLED = [
  AdminBookingStateEnum.CANCELLED,
  AdminBookingStateEnum.REFUSED,
  AdminBookingStateEnum.SENT,
  AdminBookingStateEnum.FAILED,
  AdminBookingStateEnum.REPLACED,
];

const SectionTitleContainer = styled(Stack)`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const SectionTitleText = styled(Text).attrs({ type: 'h2' })`
  font-weight: 700;
`;

const FieldsContainer = styled.div`
  margin-top: ${({ theme }) => theme.spacing()};
`;

const RoomTitle = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: gray;
  ${props =>
    props.condition &&
    css`
      color: #000;
    `}
`;

const fetchOtherBookings = async ({ bookingIds }) => {
  if (!bookingIds || bookingIds.length === 0) return Promise.resolve([]);

  const promises = bookingIds.map(id => getBooking({ bookingId: id }));
  return Promise.all(promises);
};

const BookingForm = ({
  booking,
  reload,
  tripExperiencesBooked,
  tripRoomsBooked,
  hasTrip,
  leftColumnContent = null,
}) => {
  const { t } = useTranslation();
  const { notify } = useSnack();
  const { sendErrorSnack } = useError();
  const isDisabled = IS_DISABLED.includes(booking?.state);
  const canBeUpdated =
    IS_DISABLED.includes(booking?.state) &&
    booking?.state !== AdminBookingStateEnum.SENT;
  const [requestId, setRequestId] = useState();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedRoomIndex, setSelectedRoomIndex] = useState();
  const [filters, setFilters] = useState();
  const { nationalities, resetBooking } = useBooking();
  const refreshFolder = useKoobStore(s => s.refreshFolder);

  const { data: otherBookings } = useAsync({
    promiseFn: fetchOtherBookings,
    bookingIds: booking?.groupBookingsIds,
    watch: booking?.groupBookingsIds,
  });
  const { data: tripBookings } = useAsync({
    promiseFn: getTripBookings,
    tripId: booking?.trip?.id,
    skip: !booking?.trip?.id,
  });

  const getFormTravelers = travelers => {
    return travelers.map(traveler => ({
      travelerId: traveler?.id,
      gender: traveler?.gender || '',
      firstname: traveler?.firstName || '',
      lastname: traveler?.lastName || '',
      nationality:
        nationalities.find(
          nationality =>
            !!nationality.value && nationality.value === traveler?.nationality,
        ) || '',
      passportNumber: traveler?.passportNumber || '',
      expirationDate: traveler?.expirationDate
        ? new Date(traveler?.expirationDate)
        : null,
      birthdate: traveler?.birthdate
        ? new Date(traveler?.birthdate)
        : new Date(),
      type: calculateTravelerKind(traveler),
      ageIsExact: traveler?.ageIsExact ?? null,
    }));
  };

  const roomCompositions = (booking?.roomsBooked ?? [])?.map(
    roomComposition => ({
      supplier: booking.kind,
      room: roomComposition?.room,
      initialRoom: undefined,
      previousRoom: undefined,
      travelers: getFormTravelers(roomComposition?.travelers),
    }),
  );

  const experienceCompositions = (booking?.experiencesBooked ?? []).map(
    experienceComposition => ({
      id: experienceComposition.id,
      travelers: getFormTravelers(experienceComposition?.travelers),
    }),
  );

  useEffect(() => {
    let isMounted = true;
    if (isMounted) {
      const filters = {
        bookingId: booking?.id,
        startAt: booking?.startAt,
        endAt: booking?.endAt,
        rooms: [],
      };

      roomCompositions.forEach(roomComposition => {
        const nbAdults = calculateRoomNumberOfAdult(roomComposition);
        const childrenBirthdates = getRoomChildrenBirthdate(roomComposition);

        filters.rooms.push({
          adults: nbAdults,
          childrenBirthdates: childrenBirthdates,
        });
      });

      setFilters(filters);
    }

    return () => {
      isMounted = false;
    };
  }, []);

  const handleSubmit = async values => {
    try {
      const {
        tourOperatorName,
        roomCompositions,
        experienceCompositions,
        ...otherValues
      } = values;

      let input = {
        ...otherValues,
        availabilityRequestId: requestId,
        customerLastname: values.customerLastName ?? '',
        customerFirstname: values.customerFirstName ?? '',
        customerEmail: values.customerEmail ?? '',
        customerPhoneNumber: values.customerPhoneNumber ?? '',
        customerFileId: booking?.customerFileId,
        // The field is named as in the booking tunnel, the update reads
        // operatorTourName (KD-3986).
        operatorTourName: tourOperatorName,
        toReference: values.toReference,
        roomCompositions: roomCompositions.map(currentRoomComp => {
          const roomComp = {
            roomCompositionId: currentRoomComp?.room.id,
            travelers: (currentRoomComp?.travelers || []).map(traveler => {
              const data = {
                travelerId: traveler.travelerId,
                gender: traveler.gender,
                firstName: traveler.firstname,
                lastName: traveler.lastname,
                nationality: traveler.nationality.value ?? '',
                passportNumber: traveler.passportNumber ?? '',
                birthdate: traveler.birthdate,
                ageIsExact: traveler.ageIsExact ?? null,
              };

              if (traveler.expirationDate) {
                data.expirationDate = traveler.expirationDate;
              }

              return data;
            }),
          };

          if (currentRoomComp.initialRoom) {
            roomComp.roomCompositionId = currentRoomComp.initialRoom.id;
            roomComp.newBedCompositionId = currentRoomComp.room?.bedChoice.id;
          }

          return roomComp;
        }),
        experienceCompositions: experienceCompositions.map(currentComp => {
          return {
            experienceCompositionId: currentComp?.id,
            travelers: (currentComp?.travelers || []).map(traveler => {
              const data = {
                travelerId: traveler.travelerId,
                gender: traveler.gender,
                firstName: traveler.firstname,
                lastName: traveler.lastname,
                nationality: traveler.nationality.value ?? '',
                passportNumber: traveler.passportNumber ?? '',
                birthdate: traveler.birthdate,
                ageIsExact: traveler.ageIsExact ?? null,
              };

              if (traveler.expirationDate) {
                data.expirationDate = traveler.expirationDate;
              }

              return data;
            }),
          };
        }),
      };

      await updateBooking({ bookingId: booking?.id, data: input });
      // A room change on a pending booking is not a plain edit: it answers the
      // hotel keeper's other proposal and re-submits the booking to them, so it
      // gets its own message rather than the generic "booking updated" one.
      const hasRoomChange = input.roomCompositions.some(
        roomComp => roomComp.newBedCompositionId !== undefined,
      );
      notify(
        t(
          hasRoomChange
            ? 'booking:form.newProposalSentSuccess'
            : 'booking:form.updateBookingStateSuccess',
        ),
      );
      reload();
      // Keep the sidebar active-folder card in sync after an edit (US-014).
      refreshFolder();
    } catch (err) {
      sendErrorSnack(err);
    }
  };

  const COMPOSITION_SCHEMA = Yup.array().of(
    Yup.object().shape({
      travelers: Yup.array()
        .of(
          Yup.object().shape({
            gender: Yup.string()
              .oneOf(['non_binary', 'male', 'female'])
              .required(t('errors:required')),
            firstname: Yup.string().required(t('errors:required')),
            lastname: Yup.string().required(t('errors:required')),
            birthdate: Yup.date()
              .typeError(t('errors:invalidDate'))
              .required(t('errors:required')),
          }),
        )
        .min(1),
    }),
  );

  const bedChoice = booking?.roomsBooked?.[0]?.room?.bedChoice;
  const cancellationPolicies = {
    cancelConditions:
      booking?.kind === BookingKindEnum.DMC
        ? sumDeductionByConditions(
            mergeCancelConditions(booking.roomsBooked),
            booking?.currency,
          )
        : bedChoice?.cancelConditions,
    contractingConditions: bedChoice?.contractingConditions,
  };
  const policiesData = {
    displayName: t('contractingConditions', { ns: 'booking' }),
    from: t('room.from', { ns: 'booking' }),
    to: t('room.to', { ns: 'booking' }),
    cancellationFees: t('room.cancellationFees', { ns: 'booking' }),
    currency: booking?.currency,
  };

  const linkedBookingHotels = useMemo(() => {
    if (booking?.hotel) return [];

    const startAt = dayjs(booking?.startAt);
    const endAt = dayjs(booking?.endAt);

    const isInRange = bookingData => {
      const hotelStart = dayjs(bookingData?.startAt);
      const hotelEnd = dayjs(bookingData?.endAt);
      return hotelStart.isBefore(endAt) && hotelEnd.isAfter(startAt);
    };

    const toHotelEntry = bookingData => ({
      bookingId: bookingData.id,
      hotelName: bookingData.hotel?.displayName,
      hotelId: bookingData.hotel?.id,
      state: bookingData.state,
      startAt: bookingData.startAt,
      endAt: bookingData.endAt,
    });

    if (booking?.groupBookingsIds?.length && !booking?.trip) {
      return (otherBookings ?? []).reduce((acc, response) => {
        const bookingData = response?.data;
        if (
          bookingData?.hotel &&
          bookingData?.id !== booking?.id &&
          isInRange(bookingData) &&
          [
            AdminBookingStateEnum.CONFIRMED,
            AdminBookingStateEnum.SENT,
          ].includes(bookingData?.state)
        ) {
          acc.push(toHotelEntry(bookingData));
        }
        return acc;
      }, []);
    }

    if (booking?.trip?.id) {
      return (tripBookings?.data ?? []).reduce((acc, bookingData) => {
        if (
          bookingData?.hotel &&
          bookingData?.id !== booking?.id &&
          isInRange(bookingData) &&
          [
            AdminBookingStateEnum.CONFIRMED,
            AdminBookingStateEnum.SENT,
          ].includes(bookingData?.state)
        ) {
          acc.push(toHotelEntry(bookingData));
        }
        return acc;
      }, []);
    }

    return [];
  }, [booking, otherBookings, tripBookings]);

  return (
    <Formik
      initialValues={{
        adminBookingId: booking?.bookingId,
        customerFirstName: booking?.customerFirstName || ' ',
        customerLastName: booking?.customerLastName || ' ',
        customerPhoneNumber: booking?.customerPhoneNumber || '',
        customerEmail: booking?.customerEmail || '',
        customerCompany: booking?.customerCompany || '',
        toReference: booking?.toReference ?? '',
        roomCompositions: roomCompositions ?? [],
        experienceCompositions: experienceCompositions ?? [],
        note: booking?.note || '',
        tourOperatorName: booking?.operatorTourName || '',
        networkName: booking?.networkName || '',
      }}
      validationSchema={Yup.object().shape({
        customerFirstName: Yup.string().required(t('errors:required')),
        customerLastName: Yup.string().required(t('errors:required')),
        customerEmail: Yup.string()
          .email(t('errors:email'))
          .required(t('errors:required')),
        customerPhoneNumber: Yup.string().when('roomCompositions', room => {
          if (room[0]?.supplier === 'RAKUTEN') {
            return Yup.string()
              .matches(phoneRegex, t('errors:invalidFormat'))
              .required(t('errors:required'));
          }
          return Yup.string().matches(phoneRegex, t('errors:invalidFormat'));
        }),
        roomCompositions: COMPOSITION_SCHEMA,
        experienceCompositions: COMPOSITION_SCHEMA,
      })}
      enableReinitialize={true}
      onSubmit={handleSubmit}
    >
      {({ values, isSubmitting, setFieldValue }) => (
        <Form>
          <Stack gutterSize={2} size={'full'} direction={'row'}>
            <Stack
              direction="column"
              size="full"
              style={{ flex: 2, minWidth: 0 }}
              gutterSize={2}
            >
              {hasTrip && (
                <Stack direction="column" size="full">
                  {!hasTrip && (
                    <div className="flex justify-center mt-6">
                      <i
                        className="fa-light fa-clock-rotate-left"
                        style={{ fontSize: 30 }}
                      />
                      <h1 className="text-lg ml-2 tracking-wide">
                        {t('booking:trip:emptyMessage')}
                      </h1>
                    </div>
                  )}

                  {tripRoomsBooked?.length > 0 && (
                    <KoobTitle size="text-lg mb-2">
                      {t('booking:trip:bookingType', { type: 'Hotel' })}
                    </KoobTitle>
                  )}

                  {tripRoomsBooked?.length > 0 &&
                    tripRoomsBooked.map((book, index) => (
                      <TripItem
                        key={index}
                        title={book?.hotel?.displayName}
                        kind="hotel"
                        booking={book}
                      />
                    ))}

                  {tripExperiencesBooked?.length > 0 && (
                    <KoobTitle size="text-lg mb-2">
                      {t('booking:trip:bookingType', { type: 'Experience' })}
                    </KoobTitle>
                  )}

                  {tripExperiencesBooked?.length > 0 &&
                    tripExperiencesBooked.map((book, index) => (
                      <TripItem
                        key={index}
                        title={book?.experiencesBooked[0]?.experience?.name}
                        kind="experience"
                        booking={book}
                      />
                    ))}
                </Stack>
              )}

              {!booking?.draft && (
                <Stack direction="column" gutterSize={1} size="full">
                  <ToInfos isDisabled={isDisabled} />
                  <BookingOwner
                    isDisabled={isDisabled}
                    isRequired={booking?.kind === 'RAKUTEN'}
                  />
                </Stack>
              )}

              {!hasTrip && (
                <Stack direction="column" size="full" gutterSize={2}>
                  {(values.roomCompositions || []).map(
                    (roomComposition, roomIndex) => (
                      <KoobCard key={roomIndex}>
                        <FormField
                          name={`roomCompositions.${roomIndex}.initialRoom`}
                          type="hidden"
                          disabled={true}
                        />

                        <FormField
                          name={`roomCompositions.${roomIndex}.previousRoom`}
                          type="hidden"
                          disabled={true}
                        />

                        <Stack direction="column" gutterSize={1} size="full">
                          <SectionTitleContainer>
                            <Stack>
                              <SectionTitleText>
                                {roomComposition?.room?.bedChoice
                                  ?.promotionDetails?.kind ===
                                BenefitsKindEnum.UPGRADE_ROOM
                                  ? roomComposition?.room?.bedChoice
                                      ?.promotionDetails?.upgradedToRoom
                                  : roomComposition?.room?.name}
                              </SectionTitleText>
                            </Stack>

                            <Stack>
                              <KoobButton
                                disabled={
                                  booking?.state !== 'pending' ||
                                  !roomComposition.room.otherProposal
                                }
                                type="button"
                                variant="secondary"
                                onClick={e => {
                                  e.preventDefault();
                                  setSelectedRoomIndex(roomIndex);
                                  if (roomComposition.room.otherProposal) {
                                    resetBooking();
                                  }

                                  if (!roomComposition.initialRoom) {
                                    setFieldValue(
                                      `roomCompositions.${roomIndex}.initialRoom`,
                                      roomComposition.room,
                                      true,
                                    );
                                  }

                                  setFieldValue(
                                    `roomCompositions.${roomIndex}.previousRoom`,
                                    roomComposition.room,
                                    true,
                                  );

                                  setIsOpen(true);
                                }}
                              >
                                {<i className="fa-regular fa-pen" />}
                              </KoobButton>
                            </Stack>
                          </SectionTitleContainer>

                          {(roomComposition?.travelers || [])
                            .filter(traveler => traveler.type === 'adult')
                            .map((tr, travelerIndex) => (
                              <RoomPerson
                                type={tr.type}
                                key={`adult-${travelerIndex}`}
                                name={`roomCompositions.${roomIndex}.travelers.${travelerIndex}`}
                                index={travelerIndex + 1}
                                isRoomLeader={
                                  roomIndex === 0 && travelerIndex === 0
                                }
                                disabled={isDisabled}
                                setFieldValue={setFieldValue}
                              />
                            ))}

                          {(roomComposition?.travelers || [])
                            .filter(traveler => traveler.type === 'child')
                            .map((tr, travelerIndex) => {
                              return (
                                <RoomPerson
                                  type={tr.type}
                                  key={`child-${tr.id}`}
                                  name={`roomCompositions.${roomIndex}.travelers.${
                                    travelerIndex +
                                    roomComposition.travelers.filter(
                                      traveler => traveler.type === 'adult',
                                    ).length
                                  }`}
                                  disabled={isDisabled}
                                  index={travelerIndex + 1}
                                  setFieldValue={setFieldValue}
                                />
                              );
                            })}
                        </Stack>
                      </KoobCard>
                    ),
                  )}

                  {booking?.roomsBooked && booking?.roomsBooked.length > 0 && (
                    <KoobCard>
                      <CancellationPolicies
                        rooms={
                          booking?.roomsBooked?.map(elem => elem.room) ?? []
                        }
                        bedField="bedChoice"
                      />
                      <HotelBookingPolicies
                        policies={cancellationPolicies}
                        translationData={policiesData}
                      />
                    </KoobCard>
                  )}

                  {(values.experienceCompositions || []).map(
                    (experienceComposition, roomIndex) => (
                      <KoobCard key={roomIndex}>
                        <Stack direction="column" gutterSize={1} size="full">
                          {(experienceComposition?.travelers || [])
                            .filter(traveler => traveler.type === 'adult')
                            .map((tr, travelerIndex) => (
                              <RoomPerson
                                type={tr.type}
                                key={`adult-${travelerIndex}`}
                                name={`experienceCompositions.${roomIndex}.travelers.${travelerIndex}`}
                                index={travelerIndex + 1}
                                isRoomLeader={
                                  roomIndex === 0 && travelerIndex === 0
                                }
                                disabled={isDisabled}
                                setFieldValue={setFieldValue}
                              />
                            ))}

                          {(experienceComposition?.travelers || [])
                            .filter(traveler => traveler.type === 'child')
                            .map((tr, travelerIndex) => {
                              return (
                                <RoomPerson
                                  type={tr.type}
                                  key={`child-${tr.id}`}
                                  name={`experienceCompositions.${roomIndex}.travelers.${
                                    travelerIndex +
                                    experienceComposition.travelers.filter(
                                      traveler => traveler.type === 'adult',
                                    ).length
                                  }`}
                                  disabled={isDisabled}
                                  index={travelerIndex + 1}
                                  setFieldValue={setFieldValue}
                                />
                              );
                            })}
                        </Stack>
                      </KoobCard>
                    ),
                  )}

                  {booking?.experiencesBooked?.map((experience, index) => {
                    return (
                      experience?.cancelConditions && (
                        <ExperienceCancelConditions
                          currency={booking?.currency}
                          experience={experience}
                          index={index}
                          translationKey="booking"
                        />
                      )
                    );
                  })}
                </Stack>
              )}

              <div className="w-full">{leftColumnContent}</div>
            </Stack>

            <Stack
              direction="column"
              style={{ flex: 1, minWidth: 0 }}
              gutterSize={1}
            >
              {booking && (
                <BookingSummary
                  booking={
                    !booking?.draft
                      ? booking
                      : {
                          trip: { ...booking },
                          startAt: booking?.startDate,
                          endAt: booking?.endDate,
                        }
                  }
                  values={values}
                  hasTrip={hasTrip}
                  reload={reload}
                >
                  {!hasTrip && (
                    <Stack style={{ marginBottom: '2rem' }}>
                      <FieldsContainer style={{ flex: 1 }}>
                        <RoomTitle>
                          {roomCompositions.length > 0
                            ? t('booking:form.notes')
                            : t('booking:form.experienceNotes')}
                        </RoomTitle>

                        <EditorField
                          minHeight="200px"
                          name={'note'}
                          disabled={
                            booking?.state === 'canceled' ||
                            booking?.state === 'refused'
                          }
                        />

                        <Stack alignX="flex-end" paddingTop={0.25}>
                          <Legend color="textLighter">
                            {`${t('booking:form.lastUpdate')} ${formatDateTime(
                              booking?.updatedAt,
                              'dd/MM/yy hh:mm',
                            )}`}
                          </Legend>
                        </Stack>
                      </FieldsContainer>
                    </Stack>
                  )}
                </BookingSummary>
              )}

              {!hasTrip &&
                CANCELLATION_STATUS?.[booking?.kind.toUpperCase()]?.includes(
                  booking?.state,
                ) && (
                  <CancelAdminBooking
                    adminBookingId={booking?.id}
                    kind={booking?.kind.toUpperCase()}
                    linkedBookingHotels={linkedBookingHotels}
                    reload={reload}
                  />
                )}

              {!hasTrip && (!isDisabled || booking?.state === 'sent') && (
                <KoobButton type="reset" variant="secondary" block>
                  {t('booking:form.cancel')}
                </KoobButton>
              )}

              {!hasTrip && !canBeUpdated && (
                <KoobButton
                  type="submit"
                  variant="primary"
                  size="lg"
                  block
                  loading={isSubmitting}
                >
                  {t('booking:form.save')}
                </KoobButton>
              )}
            </Stack>
            <Stack>
              {isOpen && (
                <HotelRoomCompositions
                  filters={filters}
                  hotel={booking?.hotel}
                  booking={booking}
                  setIsOpen={setIsOpen}
                  selectedRoomIndex={selectedRoomIndex}
                  roomComposition={values.roomCompositions?.[selectedRoomIndex]}
                  setFieldValue={setFieldValue}
                  setRequestId={setRequestId}
                />
              )}
            </Stack>
          </Stack>
        </Form>
      )}
    </Formik>
  );
};

export default BookingForm;
